
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Keep track of changes and updates to the Plaid API'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='API versioning and changelog' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "api-versioning"
    }}>{`API versioning`}</h4>
    <p>{`This page covers backwards-incompatible, versioned API changes. For a list of all API updates, including non-versioned ones, see the `}<a parentName="p" {...{
        "href": "/docs/changelog"
      }}>{`changelog`}</a>{`.`}</p>
    <p>{`Whenever we make a backwards-incompatible change to a general availability, non-beta product, we release a new API version to avoid causing breakages for existing developers. You can then continue to use the old API version, or update your application to upgrade to the new Plaid API version. APIs for beta products are subject to breaking changes without versioning, with 30 days notice.`}</p>
    <p>{`We consider the following changes to be backwards compatible (non-breaking):`}</p>
    <ul>
      <li parentName="ul">{`Adding new API endpoints`}</li>
      <li parentName="ul">{`Adding new optional parameters to existing endpoints`}</li>
      <li parentName="ul">{`Adding new data elements to existing response schemas or webhooks`}</li>
      <li parentName="ul">{`Adding new enum values, including `}<inlineCode parentName="li">{`error_types`}</inlineCode>{` and `}<inlineCode parentName="li">{`error_codes`}</inlineCode></li>
      <li parentName="ul">{`Subdividing existing `}<inlineCode parentName="li">{`error_codes`}</inlineCode>{` into more precise errors, and changing the http response code as necessary, as long as the error cannot be resolved via in-app logic (such as launching update mode or waiting for a few seconds and retrying) during runtime. For example, changing `}<inlineCode parentName="li">{`PRODUCT_NOT_READY`}</inlineCode>{` errors to a different `}<inlineCode parentName="li">{`error_code`}</inlineCode>{` would be a breaking change, since that error can be resolved by retrying a few seconds later, but converting an existing `}<inlineCode parentName="li">{`INTERNAL_SERVER_ERROR`}</inlineCode>{` to a more specific error would not be.`}</li>
      <li parentName="ul">{`Changing the behavior for use cases or platforms that are explicitly not supported. `}</li>
      <li parentName="ul">{`Adding new `}<inlineCode parentName="li">{`webhook_types`}</inlineCode>{` and `}<inlineCode parentName="li">{`webhook_codes`}</inlineCode></li>
      <li parentName="ul">{`Changing the length or content of any API identifier`}</li>
    </ul>
    <p>{`Customers should build their integrations to be robust to any of these above changes, as they may happen at any time. `}</p>
    <h5 {...{
      "id": "how-to-set-your-api-version"
    }}>{`How to set your API version`}</h5>
    <p>{`The default version of the API used in any requests you make can be configured in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/api"
      }}>{`Dashboard`}</a>{` and will be used when version information is not otherwise specified. You can also manually set the `}<inlineCode parentName="p">{`Plaid-Version`}</inlineCode>{` header to use a specific version for a given API request.`}</p>
    <p>{`If you're using one of the Plaid client libraries, they should all be pinned to the latest version of the API at the time when they were
released. This means that you can change the API version you're using by updating to a newer version of the client library.`}</p>
    <MDXCodeBlock type='server' title='API version example' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { Configuration, PlaidApi, PlaidEnvironments } = require('plaid');

const configuration = new Configuration({
  basePath: PlaidEnvironments[process.env.PLAID_ENV],
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const client = new PlaidApi(configuration);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] =ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.ApiClient;
import com.plaid.client.request.PlaidApi;

// Create your Plaid client
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", CLIENT_ID);
apiKeys.put("secret", SECRET);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`from plaid.api import plaid_api

configuration = plaid.Configuration(
  host=plaid.Environment.Sandbox,
  api_key={
    'clientId': PLAID_CLIENT_ID,
    'secret': PLAID_SECRET,
    'plaidVersion': '2020-09-14'
  }
)

api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "github.com/plaid/plaid-go/v48/plaid"
)

configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", {VALUE})
configuration.AddDefaultHeader("PLAID-SECRET", {VALUE})
configuration.AddDefaultHeader("Plaid-Version", "2020-09-14")
configuration.UseEnvironment(plaid.Sandbox)
client := plaid.NewAPIClient(configuration)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
    option.WithVersion(plaid.String("2020-09-14")),
)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/auth/get \\
  -H 'Content-Type: application/json' \\
  -H 'Plaid-Version: 2020-09-14'
`}</code></pre>
    </MDXCodeBlock>
  
    <h4 {...{
      "id": "version-2020-09-14"
    }}>{`Version 2020-09-14`}</h4>
    <p>{`This version includes several changes to improve authentication, streamline and simplify the API, and improve international support.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`To improve authentication, the `}<inlineCode parentName="p">{`public_key`}</inlineCode>{` has been fully removed from the API. Endpoints that previously accepted a `}<inlineCode parentName="p">{`public_key`}</inlineCode>{` for authentication, namely `}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`/institutions/search`}</inlineCode>{`, now require a `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` for authentication instead.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`/item/remove`}</inlineCode>{` no longer returns a `}<inlineCode parentName="p">{`removed`}</inlineCode>{` boolean. This field created developer confusion, because it was never possible for it to return `}<inlineCode parentName="p">{`false`}</inlineCode>{`. A failed `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` call will result in an `}<a parentName="p" {...{
            "href": "/docs/errors"
          }}>{`error`}</a>{` being returned instead.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Several undocumented and unused fields have been removed from the `}<inlineCode parentName="p">{`institution`}</inlineCode>{` object returned by the institutions endpoints `}<inlineCode parentName="p">{`/institutions/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/institutions/search`}</inlineCode>{`. The removed fields are: `}<inlineCode parentName="p">{`input_spec`}</inlineCode>{`, `}<inlineCode parentName="p">{`credentials`}</inlineCode>{`, `}<inlineCode parentName="p">{`include_display_data`}</inlineCode>{`, `}<inlineCode parentName="p">{`has_mfa`}</inlineCode>{`, `}<inlineCode parentName="p">{`mfa`}</inlineCode>{`, and `}<inlineCode parentName="p">{`mfa_code_type`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The institutions endpoints `}<inlineCode parentName="p">{`/institutions/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/institutions/search`}</inlineCode>{` now require the use of a `}<inlineCode parentName="p">{`country_codes`}</inlineCode>{` parameter and no longer use `}<inlineCode parentName="p">{`US`}</inlineCode>{` as a default value if `}<inlineCode parentName="p">{`country_codes`}</inlineCode>{` is not specified, as this behavior caused confusion and unexpected behavior for non-US developers. As part of this change, the `}<inlineCode parentName="p">{`country_codes`}</inlineCode>{` parameter has been moved out of the `}<inlineCode parentName="p">{`options`}</inlineCode>{` object and is now a top-level parameter.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`To support international payments, the response schema for the partner-only `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` endpoint has changed. The 2018-05-22 and 2019-05-29 API releases previously extended the response schema for `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` in order to support international accounts, but these changes were never extended to the `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` endpoint. This release brings the response for `}<inlineCode parentName="p">{`/processor/auth/get`}</inlineCode>{` in line with the `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` response, allowing Plaid partners to extend support for using non-ACH payment methods, such as EFT payments (Canada) or SEPA credit and direct debit (Europe). Accommodating this change does not require any code changes from Plaid developers who use partnership integrations, only from the Plaid partners themselves.`}</p>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous /processor/auth/get response",
        "#Previous": true,
        "/processor/auth/get": true,
        "response": true
      }}>{`
"numbers": [{
  "account": "9900009606",
  "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
  "routing": "011401533",
  "wire_routing": "021000021"
}]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#2020-09-14 API version /processor/auth/get response",
        "#2020-09-14": true,
        "API": true,
        "version": true,
        "/processor/auth/get": true,
        "response": true
      }}>{`"numbers": {
  "ach": [{
    "account": "9900009606",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "routing": "011401533",
    "wire_routing": "021000021"
  }],
  "eft":[{
    "account": "111122223333",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "institution": "021",
    "branch": "01140"
  }],
  "international":[{
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "bic": "NWBKGB21",
    "iban": "GB29NWBK60161331926819"
  }],
  "bacs":[{
    "account": "31926819",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "sort_code": "601613"
  }]
}
`}</code></pre>
    <h4 {...{
      "id": "version-2019-05-29"
    }}>{`Version 2019-05-29`}</h4>
    <ul>
      <li parentName="ul">{`The Auth `}<inlineCode parentName="li">{`numbers`}</inlineCode>{` schema has been extended to support Bacs (UK) and other international (IBAN and BIC) account
numbers used across the EU.`}</li>
      <li parentName="ul">{`Renamed the `}<inlineCode parentName="li">{`zip`}</inlineCode>{` field to `}<inlineCode parentName="li">{`postal_code`}</inlineCode>{` and the `}<inlineCode parentName="li">{`state`}</inlineCode>{` field to `}<inlineCode parentName="li">{`region`}</inlineCode>{` in all `}<a parentName="li" {...{
          "href": "/docs/api/products/identity/"
        }}>{`Identity`}</a>{` and
`}<a parentName="li" {...{
          "href": "/docs/api/products/transactions/"
        }}>{`Transactions`}</a>{` responses to be more international friendly.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/api/products/identity/"
        }}>{`Identity`}</a>{` objects in `}<a parentName="li" {...{
          "href": "/docs/api/products/identity/"
        }}>{`Identity responses`}</a>{` are now embedded on the `}<inlineCode parentName="li">{`owners`}</inlineCode>{` field of the
corresponding account.`}</li>
      <li parentName="ul">{`Address data fields for `}<a parentName="li" {...{
          "href": "/docs/api/products/identity/"
        }}>{`Identity`}</a>{` responses are now nullable and are returned with a null value when they
aren't available rather than an empty string.`}</li>
      <li parentName="ul">{`Added an ISO-3166-1 alpha-2 `}<inlineCode parentName="li">{`country`}</inlineCode>{` field to all `}<a parentName="li" {...{
          "href": "/docs/api/products/identity/"
        }}>{`Identity`}</a>{` and `}<a parentName="li" {...{
          "href": "/docs/api/products/transactions/"
        }}>{`Transactions`}</a>{` responses.`}</li>
      <li parentName="ul">{`The account type `}<inlineCode parentName="li">{`brokerage`}</inlineCode>{` has been renamed to `}<inlineCode parentName="li">{`investment`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`These changes are meant to enable access to International institutions for Plaid's launch in Europe and add support for
investment accounts. Test in the `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` with the new API version to see the new Schema and enable support
for International institutions from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/api"
      }}>{`Dashboard`}</a>{` in order to create Items for these institutions.`}</p>
    <h5 {...{
      "id": "auth"
    }}>{`Auth`}</h5>
    <p>{`Before, `}<inlineCode parentName="p">{`numbers`}</inlineCode>{` only had fields for `}<inlineCode parentName="p">{`ach`}</inlineCode>{` (US accounts) and `}<inlineCode parentName="p">{`eft`}</inlineCode>{` (Canadian accounts) accounts numbers:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous Auth response (2018-05-22 version)",
        "#Previous": true,
        "Auth": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true
      }}>{`"numbers": {
  "ach": [{
    "account": "9900009606",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "routing": "011401533",
    "wire_routing": "021000021"
  }],
  "eft":[{
    "account": "111122223333",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "institution": "021",
    "branch": "01140"
  }]
}
`}</code></pre>
    <p>{`Now, the structure of `}<inlineCode parentName="p">{`numbers`}</inlineCode>{` hasn't changed, but it can have `}<inlineCode parentName="p">{`ach`}</inlineCode>{`, `}<inlineCode parentName="p">{`eft`}</inlineCode>{`, `}<inlineCode parentName="p">{`bacs`}</inlineCode>{` (UK accounts), or `}<inlineCode parentName="p">{`international`}</inlineCode>{`
(currently EU accounts) numbers. Note that the schema for each of these numbers are different from one another. It is
possible for multiple networks to be present in the response.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Auth response (2019-05-29 version)",
        "#New": true,
        "Auth": true,
        "response": true,
        "(2019-05-29": true,
        "version)": true
      }}>{`"numbers": {
  "ach": [{
    "account": "9900009606",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "routing": "011401533",
    "wire_routing": "021000021"
  }],
  "eft":[{
    "account": "111122223333",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "institution": "021",
    "branch": "01140"
  }],
  "international":[{
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "bic": "NWBKGB21",
    "iban": "GB29NWBK60161331926819"
  }],
  "bacs":[{
    "account": "31926819",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "sort_code": "601613"
  }]
}
`}</code></pre>
    <h5 {...{
      "id": "identity"
    }}>{`Identity`}</h5>
    <p>{`The previous version of `}<a parentName="p" {...{
        "href": "/docs/api/products/identity/"
      }}>{`Identity`}</a>{` had US specific field names and did not include a country as part of the
location. Furthermore, the identity data was not scoped to a specific account.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous Identity response (2018-05-22 version)",
        "#Previous": true,
        "Identity": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true
      }}>{`"identity": {
  "addresses": [{
    "accounts": [
      "Plaid Checking 0000",
      "Plaid Saving 1111",
      "Plaid CD 2222"
    ],
    "data": {
      "city": "Malakoff",
      "state": "NY",
      "street": "2992 Cameron Road",
      "zip": "14236"
    },
    "primary": true
  }],
  ...
}
`}</code></pre>
    <p>{`Now, `}<inlineCode parentName="p">{`identity`}</inlineCode>{` has international friendly field names of `}<inlineCode parentName="p">{`region`}</inlineCode>{` and `}<inlineCode parentName="p">{`postal_code`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`zip`}</inlineCode>{` and `}<inlineCode parentName="p">{`state`}</inlineCode>{` as
well as an ISO-3166-1 alpha-2 `}<inlineCode parentName="p">{`country`}</inlineCode>{` field. Address data fields (`}<inlineCode parentName="p">{`city`}</inlineCode>{`, `}<inlineCode parentName="p">{`region`}</inlineCode>{`, `}<inlineCode parentName="p">{`street`}</inlineCode>{`, `}<inlineCode parentName="p">{`postal_code`}</inlineCode>{`, and
`}<inlineCode parentName="p">{`country`}</inlineCode>{`) are now nullable and are returned with a `}<inlineCode parentName="p">{`null`}</inlineCode>{` value when they aren't available rather than as an empty
string. The identity object is now available on the "owners" key of the account, which represents ownership of specific
accounts.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Identity response (2019-05-29 version)",
        "#New": true,
        "Identity": true,
        "response": true,
        "(2019-05-29": true,
        "version)": true
      }}>{`"accounts": [{
  ...
  "owners": [{
    "addresses": [{
      "data": {
        "city": "Malakoff",
        "region": "NY",
        "street": "2992 Cameron Road",
        "postal_code": "14236",
        "country": "US"
      },
      "primary": true
    }]
  }],
  ...
}]
`}</code></pre>
    <h5 {...{
      "id": "transactions"
    }}>{`Transactions`}</h5>
    <p>{`When no transactions are returned from a request, the `}<inlineCode parentName="p">{`transactions`}</inlineCode>{` object will now be `}<inlineCode parentName="p">{`null`}</inlineCode>{` rather than an empty array.`}</p>
    <p>{`In addition, the same changes to `}<a parentName="p" {...{
        "href": "/docs/api/products/identity/"
      }}>{`Identity`}</a>{` were also made to `}<a parentName="p" {...{
        "href": "/docs/api/products/transactions/"
      }}>{`Transactions`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous Transactions response (2018-05-22 version)",
        "#Previous": true,
        "Transactions": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true
      }}>{`"transactions": [{
  ...
  "location": {
    "address": "300 Post St",
    "city": "San Francisco",
    "state": "CA",
    "zip": "94108",
    "lat": null,
    "lon": null
  },
  ...
}]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Transactions response (2019-05-29 version)",
        "#New": true,
        "Transactions": true,
        "response": true,
        "(2019-05-29": true,
        "version)": true
      }}>{`"transactions": [{
  ...
  "location": {
    "address": "300 Post St",
    "city": "San Francisco",
    "region": "CA",
    "postal_code": "94108",
    "country": "US",
    "lat": null,
    "lon": null
  },
  ...
}]
`}</code></pre>
    <h5 {...{
      "id": "institutions"
    }}>{`Institutions`}</h5>
    <p>{`Changes to the institutions schema affect responses from all of the institutions API endpoints:`}</p>
    <ul>
      <li parentName="ul">{`POST `}<inlineCode parentName="li">{`/institutions/search`}</inlineCode></li>
      <li parentName="ul">{`POST `}<inlineCode parentName="li">{`/institutions/get`}</inlineCode></li>
      <li parentName="ul">{`POST `}<inlineCode parentName="li">{`/institutions/get_by_id`}</inlineCode></li>
    </ul>
    <p>{`The previous version of the Institution schema did not include the country of the institution as part of
the response.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous Institution response (2018-05-22 version)",
        "#Previous": true,
        "Institution": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true
      }}>{`"institution": {
  "credentials": [{
    "label": "User ID",
    "name": "username",
    "type": "text"
    }, {
    "label": "Password",
    "name": "password",
    "type": "password"
  }],
  "has_mfa": true,
  "institution_id": "ins_109512",
  "mfa": [
    "code",
    "list",
    "questions",
    "selections"
  ],
  "name": "Houndstooth Bank",
  "products": [
    "auth",
    "balance",
    "identity",
    "transactions"
  ],
  // included when options.include_status is true
  "status": {object}
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Institution response (2019-05-29 version)",
        "#New": true,
        "Institution": true,
        "response": true,
        "(2019-05-29": true,
        "version)": true
      }}>{`"institution": {
  "country_codes": ["US"],
  "credentials": [{
    "label": "User ID",
    "name": "username",
    "type": "text"
    }, {
    "label": "Password",
    "name": "password",
    "type": "password"
  }],
  "has_mfa": true,
  "institution_id": "ins_109512",
  "mfa": [
    "code",
    "list",
    "questions",
    "selections"
  ],
  "name": "Houndstooth Bank",
  "products": [
    "auth",
    "balance",
    "identity",
    "transactions"
  ],
  // included when options.include_status is true
  "status": {object}
}
`}</code></pre>
    <h4 {...{
      "id": "version-2018-05-22"
    }}>{`Version 2018-05-22`}</h4>
    <ul>
      <li parentName="ul">{`The Auth `}<inlineCode parentName="li">{`numbers`}</inlineCode>{` schema has changed to support ACH (US-based) and EFT (Canadian-based) account numbers`}</li>
      <li parentName="ul">{`Added the `}<inlineCode parentName="li">{`iso_currency_code`}</inlineCode>{` and `}<inlineCode parentName="li">{`unofficial_currency_code`}</inlineCode>{` fields to all `}<a parentName="li" {...{
          "href": "/docs/api/accounts/#accounts-get-response-accounts"
        }}>{`Accounts`}</a>{` and
`}<a parentName="li" {...{
          "href": "/docs/api/products/transactions/"
        }}>{`Transactions`}</a>{` responses`}</li>
    </ul>
    <p>{`Enable support for Canadian institutions from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/api"
      }}>{`Dashboard`}</a>{` and then test in the `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` using
the test Canadian institution, Tartan-Dominion Bank of Canada (institution ID `}<inlineCode parentName="p">{`ins_43`}</inlineCode>{`).`}</p>
    <p>{`Before, `}<inlineCode parentName="p">{`numbers`}</inlineCode>{` was a list of objects, each one representing the account and routing number for an ACH-eligible US
checking or savings account:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Previous Auth response (2017-03-08 version)",
        "#Previous": true,
        "Auth": true,
        "response": true,
        "(2017-03-08": true,
        "version)": true
      }}>{`"numbers": [{
  "account": "9900009606",
  "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
  "routing": "011401533",
  "wire_routing": "021000021"
}]
`}</code></pre>
    <p><strong parentName="p">{`New Auth response (2018-05-22 version)`}</strong></p>
    <p>{`Now, `}<inlineCode parentName="p">{`numbers`}</inlineCode>{` can have either `}<inlineCode parentName="p">{`ach`}</inlineCode>{` (US accounts) or `}<inlineCode parentName="p">{`eft`}</inlineCode>{` (Canadian accounts) numbers. Note that the schema for `}<inlineCode parentName="p">{`ach`}</inlineCode>{`
numbers and `}<inlineCode parentName="p">{`eft`}</inlineCode>{` numbers are different from one another.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Auth response (2018-05-22 version) for ACH numbers",
        "#New": true,
        "Auth": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true,
        "for": true,
        "ACH": true,
        "numbers": true
      }}>{`"numbers": {
   "ach": [{
    "account": "9900009606",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "routing": "011401533",
    "wire_routing": "021000021"
   }],
   "eft": []
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#New Auth response (2018-05-22 version) for EFT numbers",
        "#New": true,
        "Auth": true,
        "response": true,
        "(2018-05-22": true,
        "version)": true,
        "for": true,
        "EFT": true,
        "numbers": true
      }}>{`"numbers": {
   "ach":[],
   "eft":[{
    "account": "111122223333",
    "account_id": "vzeNDwK7KQIm4yEog683uElbp9GRLEFXGK98D",
    "institution": "021",
    "branch": "01140"
   }]
}
`}</code></pre>
    <h4 {...{
      "id": "version-2017-03-08"
    }}>{`Version 2017-03-08`}</h4>
    <p>{`The `}<inlineCode parentName="p">{`2017-03-08`}</inlineCode>{` version was the first versioned release of the API.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "api-versioning",
        level: 4,
        title: "API versioning",
      },
{
        id: "version-2020-09-14",
        level: 4,
        title: "Version 2020-09-14",
      },
{
        id: "version-2019-05-29",
        level: 4,
        title: "Version 2019-05-29",
      },
{
        id: "version-2018-05-22",
        level: 4,
        title: "Version 2018-05-22",
      },
{
        id: "version-2017-03-08",
        level: 4,
        title: "Version 2017-03-08",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
